import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import type { BuyerInfo } from "@/lib/courseBookingContext";
import { colors } from "@/theme/colors";

type Props = {
  buyerInfo: BuyerInfo;
  onChange: (buyerInfo: BuyerInfo) => void;
};

// 国際番号を考慮し余裕を持たせた電話番号の最大桁数。
const PHONE_NUMBER_MAX_LENGTH = 15;

/**
 * 購入者情報 (姓・名・電話番号・メールアドレス) の入力フォーム。
 * メールアドレス変更時は再認証が必要なため emailVerified をリセットする。
 * 姓・名はパスポート表記に合わせ英字のみ・大文字に、電話番号は数字のみに制限する。
 */
export function BuyerInfoForm({ buyerInfo, onChange }: Props) {
  const { t } = useTranslation();
  const toRomajiName = (value: string) => value.replace(/[^a-zA-Z]/g, "").toUpperCase();
  const toDigits = (value: string) => value.replace(/\D/g, "").slice(0, PHONE_NUMBER_MAX_LENGTH);
  const toEmailChars = (value: string) => value.replace(/[^a-zA-Z0-9.!#$%&'*+/=?^_`{|}~@-]/g, "");
  // 必須はアスタリスクではなくラベル内のテキストで伝える (JPTP_CANSEI-1455)。記号だけでは
  // 必須と気付きにくく、MUI が描くアスタリスクは aria-hidden のため支援技術にも届かない
  // (NPS・キャンセル理由の「（必須）」と同じ流儀)。required は input の required 属性として
  // 必須状態を伝えるため残す。
  const withRequiredLabel = (label: string) => (
    <>
      {label}
      <Box component="span" sx={{ ml: 0.5, color: colors.danger }}>
        {t("buyerInfo.requiredLabel")}
      </Box>
    </>
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        "& .MuiFormLabel-asterisk": { display: "none" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
        <TextField
          label={withRequiredLabel(t("buyerInfo.lastName"))}
          required
          value={buyerInfo.lastName}
          onChange={(e) => onChange({ ...buyerInfo, lastName: toRomajiName(e.target.value) })}
          sx={{ flex: 1, minWidth: 160 }}
        />
        <TextField
          label={withRequiredLabel(t("buyerInfo.firstName"))}
          required
          value={buyerInfo.firstName}
          onChange={(e) => onChange({ ...buyerInfo, firstName: toRomajiName(e.target.value) })}
          sx={{ flex: 1, minWidth: 160 }}
        />
      </Box>
      <TextField
        label={withRequiredLabel(t("buyerInfo.phoneNumber"))}
        required
        helperText={t("buyerInfo.phoneNumberHint")}
        value={buyerInfo.phoneNumber}
        onChange={(e) => onChange({ ...buyerInfo, phoneNumber: toDigits(e.target.value) })}
        slotProps={{ htmlInput: { inputMode: "numeric", maxLength: PHONE_NUMBER_MAX_LENGTH } }}
      />
      <TextField
        label={withRequiredLabel(t("buyerInfo.email"))}
        required
        type="email"
        helperText={t("buyerInfo.emailHint")}
        value={buyerInfo.email}
        onChange={(e) =>
          onChange({ ...buyerInfo, email: toEmailChars(e.target.value), emailVerified: false })
        }
      />
    </Box>
  );
}
